import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  createCloudIdentityGroup,
  doApiCheck,
  getAllGroupTemplates,
  getDirectoryGroupQuery,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ApiException, DirectoryGroup } from '../../../../types';
import GeneralDialog from '../../../shared/general-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import { createGroup } from '../../../api/users-api';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import axios from 'axios';
import { Messages } from '../../../utils/message-constants';
import { sortGroupTemplatesByName } from '../../../shared/group-settings.config';
import GroupLabelsCheckboxes from '../../../shared/group-labels-checkboxes';
import { groupLabels } from '../../../shared/group-labels-config';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { pagesConfig } from '../../../shared/pages-config';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DirectoryGroupFormValues,
  getGroupFormSchema,
} from './group-form-schema';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';

interface AddGroupFormProps {
  open: boolean;
  handleClose: () => void;
  domains: string[];
}

export default function AddGroupDialog({
  open,
  handleClose,
  domains,
}: AddGroupFormProps) {
  const { primaryDomain, changeLogRoles } = useUserSettings();
  const { createEvent } = useAnalytics();
  const [domain, setDomain] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const { directoryRoles } = useUserSettings();
  interface FormInput {
    name: string;
    email: string;
    description: string;
    groupTemplate: number | null;
    labels: { [key: string]: boolean };
  }

  const { data: accessGranted, isLoading: accessGrantedLoading } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    staleTime: 0,
  });

  const isAbleToEditGroupLabels = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupLabels.value
  );

  useEffect(() => {
    if (domains.length > 0) {
      const initialDomain = domains.includes(primaryDomain)
        ? primaryDomain
        : domains[0];
      setDomain(initialDomain);
    }
  }, [domains, primaryDomain]);

  const { data: groupTemplates, isLoading: templateLoading } = useQuery({
    queryKey: [UserKeys.GroupTemplates],
    queryFn: () => getAllGroupTemplates(),
    select: sortGroupTemplatesByName,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group templates'),
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    setValue,
    formState: { errors, isDirty, isValidating, isSubmitting },
  } = useForm<DirectoryGroupFormValues>({
    resolver: zodResolver(getGroupFormSchema(domain)),
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: '',
      email: '',
      description: '',
      groupTemplate: null,
      labels: {
        isMailing: true, //always true
        isSecurity: false,
        isLocked: false,
      },
    },
  });

  const labelsValue = watch('labels');
  const groupTemplateValue = watch('groupTemplate');

  const onSubmit = (data: FormInput) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.CreateGroup.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const email = data.email.toLowerCase() + '@' + domain;
      const createdGroup: DirectoryGroup = {
        name: data.name,
        email: email,
        description: data.description,
      };

      if (accessGranted) {
        cloudIdentityGroupMutate(createdGroup);
      } else {
        mutate(createdGroup);
      }
    }
  };

  function handleError(error: unknown, groupEmail?: string) {
    if (axios.isAxiosError(error)) {
      const errorResponse = error.response?.data as ApiException;
      if (
        errorResponse.message?.includes(
          'Group was successfully created but failed to apply the group template'
        ) ||
        errorResponse.message?.includes('Group was created successfully, and')
      ) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: errorResponse.message,
        });
        navigate(`/${pagesConfig.groups.link}/${groupEmail}`);
      } else if (
        errorResponse.message?.includes(
          'Cannot allow external members to join group.'
        )
      ) {
        handleErrors({
          error,
          snackbarMessage:
            'Please check the "Who can join group" field in the template or contact a Group administrator.',
        });
      } else if (
        errorResponse.message?.includes(
          'Cannot allow external members to view group content'
        )
      ) {
        handleErrors({
          error,
          snackbarMessage:
            'Please check the "Who can view conversations" field in the template or contact a Group administrator.',
        });
      } else {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Creating('this group'),
        });
      }
    } else {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('this group'),
      });
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: DirectoryGroup) => {
      const templateId = getValues('groupTemplate');

      return createGroup(
        data,
        templateId ? templateId.toString() : null,
        changeLogMessage
      );
    },
    onSuccess: async (createdGroupData: DirectoryGroup) => {
      queryClient.setQueryData(
        getDirectoryGroupQuery(createdGroupData.email, false).queryKey,
        { ...createdGroupData, directMembersCount: 0 }
      );

      const defaultLabels: { [key: string]: string } = {};
      const formLabels = getValues('labels');

      groupLabels.forEach((label) => {
        if (formLabels[label.name]) {
          defaultLabels[label.key] = '';
        }
      });

      queryClient.setQueryData(
        [UserKeys.GroupLabels, createdGroupData.email],
        defaultLabels
      );

      navigate(`/${pagesConfig.groups.link}/${createdGroupData.email}`);
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Group'),
      });
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error, data) => {
      handleError(error, data.email);
    },
    onSettled: () => {
      createEvent(analytics.directory.groups.create_group, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: cloudIdentityGroupMutate, isPending: cloudIdentityPending } =
    useMutation({
      mutationFn: (data: DirectoryGroup) => {
        const newLabels: { [key: string]: string } = {};
        const formLabels = getValues('labels');

        groupLabels.forEach((label) => {
          if (formLabels[label.name]) {
            newLabels[label.key] = '';
          }
        });

        const templateId = getValues('groupTemplate');

        const newPayload: cloudidentity_v1.Schema$Group = {
          displayName: data.name,
          description: data.description,
          groupKey: {
            id: data.email,
            namespace: '',
          },
          labels: newLabels,
        };

        return createCloudIdentityGroup(
          newPayload,
          templateId ? templateId.toString() : null,
          changeLogMessage
        );
      },
      onSuccess: (createdGroupData) => {
        // https://cloud.google.com/identity/docs/reference/rest/Shared.Types/Operation
        if (createdGroupData.done) {
          if (createdGroupData.response) {
            const group =
              createdGroupData.response as cloudidentity_v1.Schema$Group;

            // set group labels
            queryClient.setQueryData(
              [UserKeys.GroupLabels, group.groupKey?.id],
              group.labels
            );

            // set directory group
            queryClient.setQueryData(
              getDirectoryGroupQuery(group.groupKey?.id as string, false)
                .queryKey,
              {
                id: group.name?.split('/')[1],
                name: group.displayName,
                email: group.groupKey?.id,
                description: group.description,
                adminCreated: true,
                aliases: [],
                nonEditableAliases: [],
                directMembersCount: 0,
              } as DirectoryGroup
            );

            navigate(`/${pagesConfig.groups.link}/${group.groupKey?.id}`);
            setSnackbar({
              open: true,
              severity: 'success',
              message: Messages.Success.Created('Group'),
            });

            setChangeLogMessage('');
            handleClose();
            reset();
          } else {
            // there was an error
            handleError(createdGroupData.error);
          }
        } else {
          setSnackbar({
            open: true,
            severity: 'info',
            message: 'Group creation in progress...',
          });

          setChangeLogMessage('');
          handleClose();
          reset();
        }
      },
      onError: (error, data) => {
        handleError(error, data.email);
      },
      onSettled: () => {
        createEvent(analytics.directory.groups.create_group, {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.groups,
        });
        setOpenChangeLogDialog(false);
      },
    });

  return (
    <GeneralDialog
      open={open}
      maxWidth="md"
      handleClose={() => {
        reset();
        const initialDomain = domains.includes(primaryDomain)
          ? primaryDomain
          : domains[0] || '';
        setDomain(initialDomain);
        handleClose();
      }}
      title="Create Group"
      dialogContent={
        <Stack sx={{ px: 6 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field }) => (
              <TextField
                label="Name"
                fullWidth
                autoComplete="off"
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Stack flexDirection="row" alignItems="baseline" width="100%" gap={1}>
            <Controller
              name={'email'}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Email"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.email}
                  helperText={errors?.email?.message || ' '}
                  required
                  {...field}
                  slotProps={{
                    input: {
                      endAdornment: isValidating ? (
                        <CircularProgress size={20} />
                      ) : null,
                    },
                  }}
                />
              )}
            />
            <Typography>@</Typography>
            <TextField
              label="Domain"
              select
              fullWidth
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
            >
              {domains.map((domain) => (
                <MenuItem value={domain} key={domain}>
                  {domain}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack gap={2} mt={1}>
            <Controller
              name={'description'}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Description"
                  fullWidth
                  autoComplete="off"
                  {...field}
                />
              )}
            />

            {templateLoading ? (
              <Skeleton variant="text" height={60} sx={{ fontSize: '1rem' }} />
            ) : (
              <Controller
                name={'groupTemplate'}
                control={control}
                render={({ field }) => {
                  const handleTemplateChange = (newValue: number | null) => {
                    if (newValue)
                      setValue(
                        'labels',
                        { ...labelsValue, isLocked: false },
                        {
                          shouldDirty: true,
                        }
                      );
                    field.onChange(newValue || null);
                  };
                  return (
                    <Tooltip
                      arrow
                      title={
                        labelsValue.isLocked
                          ? `A template can't be selected for a locked group.`
                          : ''
                      }
                    >
                      <span>
                        <TextField
                          label={
                            groupTemplates?.length === 0
                              ? 'No group templates available'
                              : 'Group Template'
                          }
                          select
                          fullWidth
                          disabled={
                            groupTemplates?.length === 0 || labelsValue.isLocked
                          }
                          value={field.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleTemplateChange(value ? Number(value) : null);
                          }}
                          slotProps={{
                            input: {
                              endAdornment: field.value && (
                                <InputAdornment position="end" sx={{ mr: 2.5 }}>
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTemplateChange(null);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <Close />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        >
                          {groupTemplates?.map((template) => (
                            <MenuItem value={template.id} key={template.id}>
                              {template.groupTemplateName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </span>
                    </Tooltip>
                  );
                }}
              />
            )}
            <Box mt={accessGranted === true ? 2 : 0}>
              <Tooltip
                arrow
                title={
                  !isAbleToEditGroupLabels
                    ? Messages.Errors.Permission.Acting(
                        DirectoryRoles.EditGroupLabels.name
                      )
                    : groupTemplateValue
                    ? `The "Locked" label can't be set when a template is selected.`
                    : ''
                }
              >
                <span>
                  <Controller
                    name="labels"
                    control={control}
                    render={({ field }) => (
                      <GroupLabelsCheckboxes
                        isLoading={accessGrantedLoading}
                        checkedStates={labelsValue}
                        onStateChange={(name, checked) => {
                          if (name === 'isLocked' && checked) {
                            setValue('groupTemplate', null, {
                              shouldDirty: true,
                            });
                          }

                          field.onChange({
                            ...labelsValue,
                            [name]: checked,
                          });
                        }}
                        disabledStates={{
                          isMailing: true,
                          isSecurity:
                            !isAbleToEditGroupLabels || !accessGranted,
                          isLocked:
                            !isAbleToEditGroupLabels ||
                            !accessGranted ||
                            !!groupTemplateValue,
                        }}
                        accessGranted={accessGranted || false}
                      />
                    )}
                  />
                </span>
              </Tooltip>
            </Box>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const data = getValues();
              const createdGroup: DirectoryGroup = {
                name: data.name,
                email: data.email.toLowerCase() + '@' + domain,
                description: data.description,
              };

              if (accessGranted) {
                cloudIdentityGroupMutate(createdGroup);
              } else {
                mutate(createdGroup);
              }
            }}
            mutateLoading={isPending || cloudIdentityPending}
          />
        </Stack>
      }
      primaryButton={{
        loading:
          isPending || cloudIdentityPending || isValidating || isSubmitting,
        action: handleSubmit(onSubmit),
        disabled:
          !isDirty ||
          isPending ||
          cloudIdentityPending ||
          isValidating ||
          isSubmitting ||
          openChangeLogDialog,
        title: 'Add',
      }}
    />
  );
}
